import { useState, useEffect } from "react";
// Import the temp store
import { tempStore, updateTempStore } from "../utils/tempStore";

export interface EmployeeDetails {
  employeeName: string;
  employeeId: string;
  designation: string;
  department: string;
  dateOfJoining: string;
  payPeriod: string;
  basicSalary: number;
  hra: number;
  conveyance: number;
  medicalAllowance: number;
  specialAllowance: number;
  providentFund: number;
  professionalTax: number;
  incomeTax: number;
  otherDeductions: number;
  paidLeaves: number;
  unpaidLeaves: number;
}

interface PayslipFormProps {
  onGenerate: (
    details: EmployeeDetails,
    letterhead: string | null,
    signature: string | null,
    primaryColor: string,
    contentMargin: number,
    signaturePadding: number // Added to callback
  ) => void;
  initialData?: EmployeeDetails;
  initialLetterhead?: string | null;
  initialSignature?: string | null;
  initialColor?: string;
  initialMargin?: number;
  initialSignaturePadding?: number; // Added prop
}

export const PayslipForm = ({
  onGenerate,
  initialData,
  initialLetterhead,
  initialSignature,
  initialColor,
  initialMargin,
  initialSignaturePadding,
}: PayslipFormProps) => {
  const defaultState: EmployeeDetails = {
    employeeName: "",
    employeeId: "",
    designation: "",
    department: "",
    dateOfJoining: "",
    payPeriod: "",
    basicSalary: 0,
    hra: 0,
    conveyance: 0,
    medicalAllowance: 0,
    specialAllowance: 0,
    providentFund: 0,
    professionalTax: 0,
    incomeTax: 0,
    otherDeductions: 0,
    paidLeaves: 0,
    unpaidLeaves: 0,
  };

  const [signature, setSignature] = useState<string | null>(() => {
    if (initialSignature) return initialSignature;
    if (tempStore.signature) return tempStore.signature;
    return null;
  });

  // Added signaturePadding state
  const [signaturePadding, setSignaturePadding] = useState<number>(() => {
    if (initialSignaturePadding !== undefined) return initialSignaturePadding;
    if (tempStore.signaturePadding !== undefined)
      return tempStore.signaturePadding;
    return 20; // Default 20mm
  });

  // 1. INITIALIZE STATE
  const [details, setDetails] = useState<EmployeeDetails>(() => {
    if (initialData) return initialData;
    if (tempStore.details) return tempStore.details;
    return defaultState;
  });

  const [letterhead, setLetterhead] = useState<string | null>(() => {
    if (initialLetterhead) return initialLetterhead;
    if (tempStore.letterhead) return tempStore.letterhead;
    return null;
  });

  const [primaryColor, setPrimaryColor] = useState<string>(() => {
    if (initialColor) return initialColor;
    if (tempStore.primaryColor) return tempStore.primaryColor;
    return "#2563eb";
  });

  const [contentMargin, setContentMargin] = useState<number>(() => {
    if (initialMargin) return initialMargin;
    if (tempStore.contentMargin) return tempStore.contentMargin;
    return 40;
  });

  // 2. SYNC TO TEMP STORE ON EVERY CHANGE
  useEffect(() => {
    updateTempStore(
      details,
      letterhead,
      signature,
      primaryColor,
      contentMargin,
      signaturePadding // Added to sync
    );
  }, [
    details,
    letterhead,
    signature,
    primaryColor,
    contentMargin,
    signaturePadding,
  ]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height
            );
            const color = extractDominantColor(imageData);
            setPrimaryColor(color);
          }
          setLetterhead(event.target?.result as string);
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const extractDominantColor = (imageData: ImageData): string => {
    const data = imageData.data;
    const colorCount: { [key: string]: number } = {};
    for (let i = 0; i < data.length; i += 40) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a > 200 && (r < 240 || g < 240 || b < 240)) {
        const color = `rgb(${r},${g},${b})`;
        colorCount[color] = (colorCount[color] || 0) + 1;
      }
    }
    let maxCount = 0;
    let dominantColor = "rgb(37, 99, 235)";
    for (const color in colorCount) {
      if (colorCount[color] > maxCount) {
        maxCount = colorCount[color];
        dominantColor = color;
      }
    }
    return dominantColor;
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setSignature(event.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(
      details,
      letterhead,
      signature,
      primaryColor,
      contentMargin,
      signaturePadding
    );
  };

  const handleInputChange = (
    field: keyof EmployeeDetails,
    value: string | number
  ) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === "0") e.target.value = "";
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    field: keyof EmployeeDetails
  ) => {
    if (e.target.value === "") handleInputChange(field, 0);
  };

  return (
    <div className="form-card">
      <div className="flex justify-between items-center mb-6">
        <h2>Employee Payslip Generator</h2>
      </div>
      <form onSubmit={handleSubmit} className="form-container">
        {/* Letterhead Section */}
        <div className="form-group">
          <label htmlFor="letterhead" className="form-label">
            Organization Letterhead
          </label>
          <input
            id="letterhead"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="form-input"
          />
          {letterhead && (
            <div className="letterhead-preview" style={{ flexWrap: "wrap" }}>
              <img src={letterhead} alt="Letterhead" />
              <div className="color-preview-wrapper">
                <span>Extracted Color:</span>
                <div
                  className="color-preview"
                  style={{ backgroundColor: primaryColor }}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "5px",
                }}
              >
                <label
                  className="form-label"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Adjust Content Top Position</span>
                  <strong>{contentMargin}mm</strong>
                </label>
                <input
                  type="range"
                  min="20"
                  max="120"
                  step="1"
                  value={contentMargin}
                  onChange={(e) => setContentMargin(Number(e.target.value))}
                  style={{ width: "100%", cursor: "pointer" }}
                />
                <p
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  If your letterhead logo is covering the text, slide this to
                  the right.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Employee Info Section */}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Employee Name *</label>
            <input
              required
              className="form-input"
              value={details.employeeName}
              onChange={(e) =>
                handleInputChange("employeeName", e.target.value)
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Employee ID *</label>
            <input
              required
              className="form-input"
              value={details.employeeId}
              onChange={(e) => handleInputChange("employeeId", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Designation *</label>
            <input
              required
              className="form-input"
              value={details.designation}
              onChange={(e) => handleInputChange("designation", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Department *</label>
            <input
              required
              className="form-input"
              value={details.department}
              onChange={(e) => handleInputChange("department", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Date of Joining *</label>
            <input
              type="date"
              required
              className="form-input"
              value={details.dateOfJoining}
              onChange={(e) =>
                handleInputChange("dateOfJoining", e.target.value)
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Pay Period *</label>
            <input
              required
              placeholder="e.g., January 2025"
              className="form-input"
              value={details.payPeriod}
              onChange={(e) => handleInputChange("payPeriod", e.target.value)}
            />
          </div>
        </div>

        {/* Earnings Section */}
        <div className="form-section">
          <h3>Earnings</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Basic Salary *</label>
              <input
                type="number"
                required
                className="form-input"
                value={details.basicSalary}
                onChange={(e) =>
                  handleInputChange("basicSalary", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "basicSalary")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">HRA</label>
              <input
                type="number"
                className="form-input"
                value={details.hra}
                onChange={(e) =>
                  handleInputChange("hra", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "hra")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Conveyance</label>
              <input
                type="number"
                className="form-input"
                value={details.conveyance}
                onChange={(e) =>
                  handleInputChange("conveyance", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "conveyance")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Medical Allowance</label>
              <input
                type="number"
                className="form-input"
                value={details.medicalAllowance}
                onChange={(e) =>
                  handleInputChange(
                    "medicalAllowance",
                    parseFloat(e.target.value)
                  )
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "medicalAllowance")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Special Allowance</label>
              <input
                type="number"
                className="form-input"
                value={details.specialAllowance}
                onChange={(e) =>
                  handleInputChange(
                    "specialAllowance",
                    parseFloat(e.target.value)
                  )
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "specialAllowance")}
              />
            </div>
          </div>
        </div>

        {/* Leave Section */}
        <div className="form-section">
          <h3>Leave Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Paid Leaves Taken</label>
              <input
                type="number"
                min="0"
                step="any"
                className="form-input"
                value={details.paidLeaves}
                onChange={(e) =>
                  handleInputChange("paidLeaves", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "paidLeaves")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Unpaid Leaves Taken</label>
              <input
                type="number"
                min="0"
                step="any"
                className="form-input"
                value={details.unpaidLeaves}
                onChange={(e) =>
                  handleInputChange("unpaidLeaves", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "unpaidLeaves")}
              />
            </div>
          </div>
        </div>

        {/* Deductions Section */}
        <div className="form-section">
          <h3>Deductions</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Provident Fund</label>
              <input
                type="number"
                className="form-input"
                value={details.providentFund}
                onChange={(e) =>
                  handleInputChange("providentFund", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "providentFund")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Professional Tax</label>
              <input
                type="number"
                className="form-input"
                value={details.professionalTax}
                onChange={(e) =>
                  handleInputChange(
                    "professionalTax",
                    parseFloat(e.target.value)
                  )
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "professionalTax")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Income Tax</label>
              <input
                type="number"
                className="form-input"
                value={details.incomeTax}
                onChange={(e) =>
                  handleInputChange("incomeTax", parseFloat(e.target.value))
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "incomeTax")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Other Deductions</label>
              <input
                type="number"
                className="form-input"
                value={details.otherDeductions}
                onChange={(e) =>
                  handleInputChange(
                    "otherDeductions",
                    parseFloat(e.target.value)
                  )
                }
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, "otherDeductions")}
              />
            </div>
          </div>
        </div>

        {/* Digital Signature Section */}
        <div className="form-group" style={{ marginTop: "1rem" }}>
          <label htmlFor="signature" className="form-label">
            Digital Signature (Transparent PNG recommended)
          </label>
          <input
            id="signature"
            type="file"
            accept="image/*"
            onChange={handleSignatureUpload}
            className="form-input"
          />
          {signature && (
            <div
              className="letterhead-preview"
              style={{ flexWrap: "wrap", marginTop: "10px" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <img
                  src={signature}
                  alt="Signature"
                  style={{ height: "3rem", border: "1px dashed #ccc" }}
                />
                <button
                  type="button"
                  onClick={() => setSignature(null)}
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginLeft: "10px",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                  }}
                >
                  Remove
                </button>
              </div>

              {/* Added Signature Adjustment Slider */}
              <div
                style={{
                  width: "100%",
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#fef2f2",
                  borderRadius: "5px",
                }}
              >
                <label
                  className="form-label"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Adjust Signature Bottom Position</span>
                  <strong>{signaturePadding}mm</strong>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={signaturePadding}
                  onChange={(e) => setSignaturePadding(Number(e.target.value))}
                  style={{ width: "100%", cursor: "pointer" }}
                />
                <p
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  Slide this to move the signature up if it overlaps with your
                  letterhead footer.
                </p>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary btn-submit">
          Generate Payslip
        </button>
      </form>
    </div>
  );
};
